import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM",
  "NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA",
  "WV","WI","WY","PR","VI","GU",
];

/**
 * Quick-clone modal — clone any state-scoped buyer destination for a new
 * state + buyer email in one form. Everything else (template, campaigns,
 * digest config, retry policy) is copied verbatim from `source`.
 *
 * Paused-by-default so ops reviews the cloned config before it fires.
 *
 * Iter-30: If the server detects a state overlap with another active
 * destination (409 state_overlap), we surface the list of conflicts inline
 * and let the admin re-submit with `?force=true` to proceed anyway.
 */
export default function QuickCloneModal({ source, onClose, onSaved }) {
  const [stateCode, setStateCode] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [firmName, setFirmName] = useState("");
  const [busy, setBusy] = useState(false);
  const [overlaps, setOverlaps] = useState(null);

  const suggestedName = useMemo(() => {
    if (!source) return "";
    const st = (stateCode || "??").toUpperCase();
    if (source.name && / — [A-Z]{2} Only$/.test(source.name)) {
      return source.name.replace(/ — [A-Z]{2} Only$/, ` — ${st} Only`);
    }
    return `${source.name || "Buyer"} — ${st}`;
  }, [source, stateCode]);

  const doSave = async (force) => {
    const code = stateCode.trim().toUpperCase();
    if (!code || code.length !== 2) return toast.error("Enter a 2-letter US state code");
    if (!US_STATES.includes(code)) return toast.error(`${code} is not a valid US state`);
    if (!buyerEmail.includes("@")) return toast.error("Valid buyer email required");
    setBusy(true);
    try {
      const cfg = { ...(source.config || {}) };
      cfg.to = buyerEmail;
      cfg.state_allowlist = [code];
      if (cfg.daily_digest_enabled) cfg.daily_digest_recipient = buyerEmail;
      delete cfg.daily_digest_last_sent_at;
      delete cfg.reply_to;
      const finalName = firmName.trim() || suggestedName;
      const url = force
        ? "/delivery/destinations?force=true"
        : "/delivery/destinations";
      await api.post(url, {
        client_id: null,
        name: finalName,
        type: source.type,
        config: cfg,
        campaign_ids: source.campaign_ids || [],
        is_active: false,
        is_default: false,
        retry_policy: { max_attempts: source.retry_policy?.max_attempts || 5 },
      });
      toast.success(`Cloned to ${code} — paused. Review, then flip Active on.`);
      onSaved?.();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === "state_overlap") {
        setOverlaps(detail.overlaps || []);
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Clone failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  const save = (e) => {
    e.preventDefault();
    setOverlaps(null);
    doSave(false);
  };

  const proceedAnyway = () => {
    doSave(true);
  };

  if (!source) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="quick-clone-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={save}
        className="bg-white border-4 border-black w-full max-w-lg p-6 brut-shadow-sm"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="label-mono">Quick clone with overrides</div>
            <h2 className="font-chivo font-black text-2xl uppercase tracking-tighter leading-tight">
              Clone for a new state
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="quick-clone-close"
            className="border border-black p-1 hover:bg-black hover:text-white"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <p className="text-xs text-zinc-600 mb-4 leading-relaxed">
          Copies <strong>{source.name}</strong> — same template, campaigns, digest
          settings, retry policy — then swaps in the new state code and buyer email.
          Paused by default so you can review before it starts firing.
        </p>

        <label className="label-mono">State code</label>
        <input
          data-testid="quick-clone-state"
          maxLength={2}
          value={stateCode}
          onChange={(e) => { setStateCode(e.target.value.toUpperCase()); setOverlaps(null); }}
          className="w-full border border-black px-3 py-2 mt-1 font-mono uppercase text-lg tracking-widest"
          placeholder="GA"
          list="quick-clone-state-list"
          autoFocus
        />
        <datalist id="quick-clone-state-list">
          {US_STATES.map((s) => <option key={s} value={s} />)}
        </datalist>

        <label className="label-mono mt-3 block">Buyer email</label>
        <input
          data-testid="quick-clone-email"
          type="email"
          value={buyerEmail}
          onChange={(e) => setBuyerEmail(e.target.value)}
          className="w-full border border-black px-3 py-2 mt-1"
          placeholder="ops@newbuyer.com"
        />

        <label className="label-mono mt-3 block">Firm name (optional)</label>
        <input
          data-testid="quick-clone-name"
          value={firmName}
          onChange={(e) => setFirmName(e.target.value)}
          className="w-full border border-black px-3 py-2 mt-1"
          placeholder={suggestedName}
        />

        <div className="mt-4 border border-black bg-zinc-50 p-3 text-[11px] text-zinc-700">
          <div className="label-mono mb-1">Preview</div>
          <div><strong>Name:</strong> {firmName || suggestedName}</div>
          <div><strong>Type:</strong> {source.type}</div>
          <div><strong>State allowlist:</strong> [{stateCode || "??"}]</div>
          <div><strong>Delivery to:</strong> {buyerEmail || "(not set)"}</div>
          <div><strong>Template:</strong> {source.config?.template || "default"}</div>
          <div><strong>Digest:</strong> {source.config?.daily_digest_enabled ? "on" : "off"}</div>
          <div><strong>Active on save:</strong> <span className="text-amber-700">no (paused)</span></div>
        </div>

        {overlaps && overlaps.length > 0 && (
          <div
            className="mt-4 border-2 border-amber-700 bg-amber-50 p-3 text-[11px]"
            data-testid="quick-clone-overlap-warning"
          >
            <div className="flex items-center gap-2 mb-2 text-amber-900 font-bold">
              <Warning size={14} weight="bold" />
              <span className="uppercase tracking-widest text-[10px]">
                Duplicate state guard — {overlaps.length} destination(s) already cover this state
              </span>
            </div>
            <ul className="space-y-1 text-amber-900">
              {overlaps.map((o) => (
                <li key={o.destination_id} className="border border-amber-700/50 bg-white px-2 py-1">
                  <strong>{o.name}</strong>
                  {o.recipient ? <span className="text-zinc-600"> · {o.recipient}</span> : null}
                  <span className="ml-2 font-mono">[{o.overlapping_states.join(", ")}]</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-amber-900">
              Proceeding will fan the same lead out to <strong>{overlaps.length + 1}</strong> destinations.
              This is usually a mistake — either pick a different state, or confirm you really want that.
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOverlaps(null)}
                className="border border-black px-3 py-1 uppercase text-[10px] tracking-widest hover:bg-zinc-100"
                data-testid="quick-clone-overlap-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={proceedAnyway}
                disabled={busy}
                data-testid="quick-clone-overlap-force"
                className="bg-amber-700 text-white border border-amber-700 px-3 py-1 uppercase text-[10px] tracking-widest font-bold disabled:opacity-50"
              >
                {busy ? "Cloning…" : "Proceed anyway"}
              </button>
            </div>
          </div>
        )}

        {(!overlaps || overlaps.length === 0) && (
          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              className="border border-black px-4 py-2 uppercase text-xs tracking-widest hover:bg-zinc-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="quick-clone-submit"
              className="bg-black text-white border border-black px-4 py-2 uppercase text-xs tracking-widest font-bold disabled:opacity-50"
            >
              {busy ? "Cloning…" : "Clone (paused)"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
